import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AlphaCoreLogo from "@/components/AlphaCoreLogo";

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, pwd);
      toast.success("Welcome back to AlphaCore");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0B0B0D] text-white">
      {/* Left visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 kr-grid-bg opacity-50" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.2), transparent 60%)" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}>
            <AlphaCoreLogo variant="hero" size={420} className="mx-auto" />
          </motion.div>
          <div className="kr-overline text-[#00E5FF] mt-4">THE CORE OF INTELLIGENCE</div>
          <h2 className="font-display text-3xl mt-3 max-w-md mx-auto leading-tight tracking-tight">
            Your agents are <br /> already at work.
          </h2>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="p-4 sm:p-6 flex items-center justify-between border-b border-white/5 gap-3">
          <Link to="/" className="shrink-0"><AlphaCoreLogo size={36} /></Link>
          <Link to="/signup" className="text-sm text-white/60 hover:text-white kr-link truncate" data-testid="link-to-signup">New here? Sign up →</Link>
        </div>
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
            <div className="kr-overline text-white/40">[ SIGN IN ]</div>
            <h1 className="font-display text-3xl mt-2 tracking-tight">Welcome back.</h1>
            <p className="text-white/55 text-sm mt-2">Sign in to your AlphaCore workspace.</p>

            <Button
              type="button"
              onClick={loginWithGoogle}
              className="w-full mt-8 bg-white text-black hover:bg-white/90 rounded-md h-11 font-medium"
              data-testid="login-google-btn"
            >
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 my-7">
              <div className="flex-1 h-px bg-white/10" />
              <span className="kr-overline">or with email</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="kr-overline">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                  <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="pl-9 bg-[#0A0A0E] border-white/10 h-11" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="kr-overline">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-white/50 hover:text-white" data-testid="forgot-link">Forgot?</Link>
                </div>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                  <Input data-testid="login-password-input" type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="••••••••" className="pl-9 bg-[#0A0A0E] border-white/10 h-11" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full ac-cta h-11 mt-2" data-testid="login-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
